import type { TemplateDocument } from '@k2/canvas/document'
import type { Ratio } from '@k2/domain/campaigns'
import { useApi } from '@/api'
import type { useCopyResource } from './copy.resource'

type CopySectionRow = NonNullable<Awaited<ReturnType<ReturnType<typeof useCopyResource>['getCopy']>>['data']>['sections'][number]

// Hand-shaped, not derived from `useSlidesResource`'s own return type: `restoreSlide` below takes one as a
// parameter, and a type built from the hook's return would have to resolve that return before it could
// resolve the parameter that is part of it — a cycle. Matches `slideRestoreSnapshot` in
// `apps/api/src/canvas/slides.controller.ts`, which every route in this file that carries a snapshot uses.
export interface SlideSnapshot {
  slide: {
    id: string
    deliverableId: string
    ordinal: number
    ratio: Ratio
    frameId: string | null
    document: unknown
    assembledAt: string
  }
  group: readonly CopySectionRow[] | null
}

export const useSlidesResource = () => {
  const api = useApi()
  const deliverableOf = (organizationId: string, deliverableId: string) =>
    api.api.v1.organizations({ organizationId }).deliverables({ deliverableId })
  const slideOf = (organizationId: string, slideId: string) => api.api.v1.organizations({ organizationId }).slides({ slideId })

  const delSlide = (organizationId: string, slideId: string) => slideOf(organizationId, slideId).delete()

  return {
    getSlides: (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId).slides.get(),
    setSlide: (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId).slides.post({}),
    setSlideOrder: (organizationId: string, deliverableId: string, order: string[]) =>
      deliverableOf(organizationId, deliverableId).slides.patch({ order }),
    setDeliverableRatio: (organizationId: string, deliverableId: string, ratio: Ratio) =>
      deliverableOf(organizationId, deliverableId).ratio.patch({ ratio }),
    delSlide,
    // FR-F7: re-inserts the exact slide `DELETE` returned — id, ordinal, document
    // and copy group alike — so an undone removal comes back whole (decision 9, decision 14).
    restoreSlide: (organizationId: string, deliverableId: string, snapshot: SlideSnapshot) =>
      deliverableOf(organizationId, deliverableId).slides.restore.post({
        // A defensive copy, not a cast: the delete response's `group` is a readonly array, and the restore
        // body's is not.
        snapshot: { slide: snapshot.slide, group: snapshot.group ? [...snapshot.group] : null }
      }),
    setSlideFrame: (organizationId: string, slideId: string, frameId: string | null) =>
      slideOf(organizationId, slideId).frame.patch({ frameId }),
    getCanvasDocument: (organizationId: string, slideId: string) => slideOf(organizationId, slideId)['canvas-document'].get(),
    setCanvasDocument: (organizationId: string, slideId: string, document: TemplateDocument) =>
      slideOf(organizationId, slideId)['canvas-document'].put({ document }),
    getFrameCandidates: (organizationId: string, deliverableId: string) =>
      deliverableOf(organizationId, deliverableId)['frame-candidates'].get(),
    uploadFrameCandidate: (organizationId: string, deliverableId: string, file: File) =>
      deliverableOf(organizationId, deliverableId)['frame-candidates'].upload.post({ file }),
    uploadCanvasImage: (organizationId: string, deliverableId: string, file: File) =>
      deliverableOf(organizationId, deliverableId)['canvas-images'].post({ file }),
    uploadChatReference: (organizationId: string, deliverableId: string, file: File) =>
      deliverableOf(organizationId, deliverableId)['chat-references'].post({ file }),
    getAssetLibrary: (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId)['asset-library'].get(),
    linkFrameCandidate: (organizationId: string, deliverableId: string, assetId: string) =>
      deliverableOf(organizationId, deliverableId)['frame-candidates'].link.post({ assetId }),
    setStartFromConcept: (organizationId: string, deliverableId: string, conceptId: string) =>
      deliverableOf(organizationId, deliverableId)['start-from-concept'].post({ conceptId })
  }
}
